import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { WorkItem } from '../../types/models';

const SHOWN = 5;

const since = (item: WorkItem) =>
  item.originalDate ? `Since ${format(new Date(`${item.originalDate.slice(0, 10)}T12:00:00`), 'd MMM')}` : '';

/**
 * "Still doing these?" Items carried forward for more than two weeks, oldest
 * first, each with a decision: keep it (its carry-over clock restarts) or drop
 * it. Delegating, or anything else, happens on the item itself.
 */
export function StillDoingList({ items, busyIds, onKeep, onDrop, onOpenAll, wide = false }: {
  items: WorkItem[];
  busyIds: ReadonlySet<string>;
  onKeep: (item: WorkItem) => void;
  onDrop: (item: WorkItem) => void;
  onOpenAll: () => void;
  /** Full-width: each item on one line, its buttons beside it (from small screens up). */
  wide?: boolean;
}) {
  if (items.length === 0) return null;
  const shown = items.slice(0, SHOWN);
  const more = items.length - shown.length;
  return (
    <section aria-labelledby="still-doing-title">
      <h3 id="still-doing-title" className="text-sm font-semibold">Still doing these?</h3>
      <p className="mb-2 text-xs text-muted-foreground">
        {items.length === 1
          ? '1 item has rolled over for two weeks or more.'
          : `${items.length} items have rolled over for two weeks or more.`}{' '}
        They're out of today's list until you decide.
      </p>
      <ul className="flex flex-col gap-2">
        {shown.map(item => (
          <li key={item.id} className={cn('rounded-lg border border-border p-2.5', wide && 'sm:flex sm:items-center sm:gap-3')}>
            <p className={cn('text-sm leading-snug', wide && 'sm:min-w-0 sm:flex-1')}>
              {item.title}
              {wide && since(item) && <span className="hidden text-xs text-muted-foreground sm:inline"> · {since(item)}</span>}
            </p>
            <div className={cn('mt-1.5 flex items-center justify-between gap-2', wide && 'sm:mt-0 sm:shrink-0')}>
              <span className={cn('text-xs text-muted-foreground', wide && 'sm:hidden')}>{since(item)}</span>
              <span className="flex gap-1.5">
                <Button size="sm" variant="outline" className="h-7" disabled={busyIds.has(item.id)}
                  aria-label={`Still doing it: ${item.title}`}
                  onClick={() => onKeep(item)}>
                  Still doing it
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-muted-foreground" disabled={busyIds.has(item.id)}
                  aria-label={`Drop: ${item.title}`}
                  onClick={() => onDrop(item)}>
                  Drop
                </Button>
              </span>
            </div>
          </li>
        ))}
      </ul>
      {more > 0 && (
        <Button variant="link" size="sm" className="mt-1 h-auto px-0" onClick={onOpenAll}>
          {more} more in Work
        </Button>
      )}
    </section>
  );
}
